/* ──────────────────────────────────────────────────────────────
   SectionComponents.jsx  –  Mall Theme Engine + Section Renderers
   12 themes  ·  14 section types  ·  ShopLayout (side/top nav)
   ────────────────────────────────────────────────────────────── */
import React, { useState, useEffect } from "react";
import { sanitizeHtml, safeHref } from "../../utils/safeHtml";
import { toAbsUrl } from "../../utils/api";
import { useShopMallCart } from "../../context/ShopMallCartContext";
import MallCartDrawer from "../MallCartDrawer";
import {
  ChevronDown,
  ChevronRight,
  Search,
  Menu,
  X,
  Star,
  Tag,
  Percent,
  ExternalLink,
  Play,
  Quote,
  ArrowRight,
  Flame,
  Sparkles,
  ShoppingBag,
  Home,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   THEMES  –  12 curated palettes
   ═══════════════════════════════════════════════════════════════ */
export const THEMES = {
  /* ── Dark ─────────────────────────────── */
  midnight: {
    id: "midnight",
    label: "Midnight",
    group: "Dark",
    bg: "#09090b",
    card: "#18181b",
    text: "#fafafa",
    muted: "#a1a1aa",
    primary: "#8b5cf6",
    primaryHover: "#7c3aed",
    border: "#27272a",
    accent: "#c084fc",
    font: "'Inter', sans-serif",
    navStyle: "side",
    gradient: "from-violet-600/20 to-purple-900/20",
  },
  neon: {
    id: "neon",
    label: "Neon",
    group: "Dark",
    bg: "#0a0a0a",
    card: "#141414",
    text: "#f0f0f0",
    muted: "#888888",
    primary: "#00ff88",
    primaryHover: "#00dd77",
    border: "#222222",
    accent: "#00ffcc",
    font: "'Inter', sans-serif",
    navStyle: "top",
    gradient: "from-emerald-500/20 to-teal-900/20",
  },
  royal: {
    id: "royal",
    label: "Royal",
    group: "Dark",
    bg: "#0c0a1a",
    card: "#1a1530",
    text: "#f5f0ff",
    muted: "#9b8ec4",
    primary: "#d4af37",
    primaryHover: "#c9a02e",
    border: "#2d2550",
    accent: "#ffd700",
    font: "'Georgia', serif",
    navStyle: "side",
    gradient: "from-amber-500/20 to-purple-900/20",
  },
  slate: {
    id: "slate",
    label: "Slate",
    group: "Dark",
    bg: "#0f1419",
    card: "#1c2530",
    text: "#e7e9ea",
    muted: "#8899a6",
    primary: "#1d9bf0",
    primaryHover: "#1a8cd8",
    border: "#2f3940",
    accent: "#79c0ff",
    font: "'Inter', sans-serif",
    navStyle: "top",
    gradient: "from-sky-500/20 to-slate-900/20",
  },

  /* ── Ocean / Cool ────────────────────── */
  ocean: {
    id: "ocean",
    label: "Ocean",
    group: "Cool",
    bg: "#0f172a",
    card: "#1e293b",
    text: "#f1f5f9",
    muted: "#94a3b8",
    primary: "#0ea5e9",
    primaryHover: "#0284c7",
    border: "#334155",
    accent: "#38bdf8",
    font: "'Inter', sans-serif",
    navStyle: "top",
    gradient: "from-sky-500/20 to-blue-900/20",
  },
  arctic: {
    id: "arctic",
    label: "Arctic",
    group: "Cool",
    bg: "#f0f9ff",
    card: "#ffffff",
    text: "#0c4a6e",
    muted: "#64748b",
    primary: "#0284c7",
    primaryHover: "#0369a1",
    border: "#bae6fd",
    accent: "#0ea5e9",
    font: "'Inter', sans-serif",
    navStyle: "top",
    gradient: "from-sky-100 to-blue-50",
  },

  /* ── Warm ─────────────────────────────── */
  sunrise: {
    id: "sunrise",
    label: "Sunrise",
    group: "Warm",
    bg: "#fff7ed",
    card: "#ffffff",
    text: "#1c1917",
    muted: "#78716c",
    primary: "#f97316",
    primaryHover: "#ea580c",
    border: "#fed7aa",
    accent: "#fb923c",
    font: "'Inter', sans-serif",
    navStyle: "top",
    gradient: "from-orange-100 to-amber-50",
  },
  coral: {
    id: "coral",
    label: "Coral",
    group: "Warm",
    bg: "#fff1f2",
    card: "#ffffff",
    text: "#1f1215",
    muted: "#a8a29e",
    primary: "#f43f5e",
    primaryHover: "#e11d48",
    border: "#fecdd3",
    accent: "#fb7185",
    font: "'Inter', sans-serif",
    navStyle: "side",
    gradient: "from-rose-100 to-pink-50",
  },

  /* ── Nature ──────────────────────────── */
  forest: {
    id: "forest",
    label: "Forest",
    group: "Nature",
    bg: "#052e16",
    card: "#14532d",
    text: "#f0fdf4",
    muted: "#86efac",
    primary: "#22c55e",
    primaryHover: "#16a34a",
    border: "#166534",
    accent: "#4ade80",
    font: "'Inter', sans-serif",
    navStyle: "side",
    gradient: "from-green-600/20 to-emerald-900/20",
  },
  earth: {
    id: "earth",
    label: "Earth",
    group: "Nature",
    bg: "#1c1a17",
    card: "#292524",
    text: "#fafaf9",
    muted: "#a8a29e",
    primary: "#d97706",
    primaryHover: "#b45309",
    border: "#44403c",
    accent: "#fbbf24",
    font: "'Georgia', serif",
    navStyle: "side",
    gradient: "from-amber-600/20 to-stone-900/20",
  },

  /* ── Pastel / Light ──────────────────── */
  lavender: {
    id: "lavender",
    label: "Lavender",
    group: "Pastel",
    bg: "#faf5ff",
    card: "#ffffff",
    text: "#2e1065",
    muted: "#7c3aed",
    primary: "#a855f7",
    primaryHover: "#9333ea",
    border: "#e9d5ff",
    accent: "#c084fc",
    font: "'Inter', sans-serif",
    navStyle: "top",
    gradient: "from-purple-100 to-violet-50",
  },
  candy: {
    id: "candy",
    label: "Candy",
    group: "Pastel",
    bg: "#fdf2f8",
    card: "#ffffff",
    text: "#4a044e",
    muted: "#a21caf",
    primary: "#ec4899",
    primaryHover: "#db2777",
    border: "#fbcfe8",
    accent: "#f472b6",
    font: "'Inter', sans-serif",
    navStyle: "top",
    gradient: "from-pink-100 to-fuchsia-50",
  },
};

/* helper: is the theme dark? */
const isDark = (theme) => {
  const bg = theme?.bg || "#000";
  const hex = bg.replace("#", "");
  if (hex.length < 6) return true;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return r * 0.299 + g * 0.587 + b * 0.114 < 128;
};

/* ═══════════════════════════════════════════════════════════════
   NAVIGATION  –  Sidebar / Top-bar items
   ═══════════════════════════════════════════════════════════════ */
function SidebarNavItem({ item, theme, depth = 0, onNavigate }) {
  const [open, setOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const pl = 16 + depth * 16;

  const handleClick = () => {
    if (hasChildren) {
      setOpen(!open);
    } else if (item.onClick) {
      item.onClick();
    } else if (item.url && onNavigate) {
      onNavigate(item.url);
    }
  };

  return (
    <div>
      <button
        onClick={handleClick}
        className="w-full flex items-center justify-between text-left text-sm transition-colors duration-150"
        style={{
          paddingLeft: pl,
          paddingRight: 12,
          paddingTop: depth > 0 ? 8 : 10,
          paddingBottom: depth > 0 ? 8 : 10,
          color: item.active ? theme.primary : theme.muted,
          borderLeft: depth === 0 ? `2px solid ${item.active ? theme.primary : 'transparent'}` : undefined,
          backgroundColor: item.active ? theme.primary + '10' : undefined,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = theme.primary;
          e.currentTarget.style.backgroundColor = theme.primary + "10";
          if (depth === 0)
            e.currentTarget.style.borderLeftColor = theme.primary;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = item.active ? theme.primary : theme.muted;
          e.currentTarget.style.backgroundColor = item.active ? theme.primary + '10' : "transparent";
          if (depth === 0)
            e.currentTarget.style.borderLeftColor = item.active ? theme.primary : "transparent";
        }}
      >
        <span className="flex items-center gap-2 truncate font-medium">
          {item.icon && <item.icon size={15} />}
          {/* Show a tiny product thumbnail for category items (depth > 0) */}
          {depth > 0 && item.image && (
            <img
              src={toAbsUrl(item.image)}
              alt=""
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                objectFit: 'cover',
                flexShrink: 0,
                border: `1px solid ${theme.border || 'rgba(0,0,0,0.1)'}`,
              }}
            />
          )}
          {item.label}
        </span>
        {hasChildren &&
          (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
      </button>
      {hasChildren && open && (
        <div className="overflow-hidden">
          {item.children.map((child, i) => (
            <SidebarNavItem
              key={i}
              item={child}
              theme={theme}
              depth={depth + 1}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TopNavItem({ item, theme }) {
  const [open, setOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button
        onClick={() => (hasChildren ? setOpen(!open) : null)}
        onMouseEnter={() => hasChildren && setOpen(true)}
        className="flex items-center gap-1 text-sm font-medium px-3 py-2 rounded-lg transition-all duration-200"
        style={{ color: theme.muted }}
        onMouseOver={(e) => {
          e.currentTarget.style.color = theme.primary;
          e.currentTarget.style.backgroundColor = theme.primary + "12";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.color = theme.muted;
          e.currentTarget.style.backgroundColor = "transparent";
        }}
      >
        <span className="truncate">{item.label}</span>
        {hasChildren && (
          <ChevronDown
            size={12}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {hasChildren && open && (
        <div
          className="absolute left-0 top-full mt-1 min-w-[200px] rounded-xl shadow-2xl border z-50 py-2"
          style={{
            background: theme.card,
            borderColor: theme.border,
          }}
        >
          {item.children.map((child, i) => (
            <a
              key={i}
              href={safeHref(child.url) || "#"}
              className="flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-150"
              style={{ color: theme.text }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.primary + "12";
                e.currentTarget.style.color = theme.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = theme.text;
              }}
            >
              {child.image && (
                <img
                  src={toAbsUrl(child.image)}
                  alt=""
                  className="w-8 h-8 rounded-lg object-cover"
                />
              )}
              <span>{child.label}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SHOP LAYOUT  –  wraps all sections, handles side / top nav
   ═══════════════════════════════════════════════════════════════ */
export function ShopLayout({
  theme,
  shopName,
  isMobile,
  navLinks = [],
  collections = [],       // [{ id, label, emoji, productIds }]
  activeCollection = "",  // id of active collection
  onCollection,           // (id) => void
  exitUrl = "/",          // where "Exit Shop" goes
  searchQuery = "",
  onSearch,
  onHome,
  onNavigate,
  children,
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const dark = isDark(theme);

  // Cart state
  const { count: cartCount, isOpen: cartOpen, openCart, closeCart } = useShopMallCart();

  useEffect(() => setLocalSearch(searchQuery), [searchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onSearch?.(localSearch);
  };

  const searchBar = (
    <form onSubmit={handleSearchSubmit} className="relative w-full max-w-xs">
      <Search
        size={16}
        className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ color: theme.muted }}
      />
      <input
        value={localSearch}
        onChange={(e) => setLocalSearch(e.target.value)}
        placeholder="Search products…"
        className="w-full pl-9 pr-3 py-2 rounded-xl text-sm border outline-none transition-all duration-200"
        style={{
          background: dark ? theme.card : theme.bg,
          color: theme.text,
          borderColor: theme.border,
        }}
      />
    </form>
  );

  /* ── SIDE NAV ── */
  if (theme.navStyle === "side" && !isMobile) {
    return (
      <div
        className="flex min-h-screen"
        style={{ background: theme.bg, fontFamily: theme.font }}
      >
        {/* sidebar */}
        <aside
          className="w-56 flex-shrink-0 flex flex-col border-r sticky top-0 h-screen overflow-y-auto"
          style={{ background: theme.card, borderColor: theme.border }}
        >
          <button
            onClick={onHome}
            className="w-full px-4 py-5 flex items-center gap-2 border-b text-left cursor-pointer hover:opacity-80 transition-opacity"
            style={{ borderColor: theme.border }}
          >
            <ShoppingBag size={18} style={{ color: theme.primary }} />
            <span
              className="font-bold text-sm truncate"
              style={{ color: theme.text }}
            >
              {shopName}
            </span>
          </button>
          <div className="px-3 py-3">{searchBar}</div>
          <nav className="flex-1 py-2 space-y-0.5 overflow-y-auto">
            {/* Home link — always first */}
            <SidebarNavItem
              item={{ label: "Home", icon: Home, onClick: onHome, active: !searchQuery && !activeCollection && navLinks.every(l => !l.active) }}
              theme={theme}
            />
            {navLinks.map((item, i) => (
              <SidebarNavItem key={i} item={item} theme={theme} onNavigate={onNavigate} />
            ))}

            {/* ── Custom Collections ── */}
            {collections.length > 0 && (
              <>
                <div className="mx-3 my-2 border-t" style={{ borderColor: theme.border }} />
                <p className="px-4 pb-1 text-[10px] uppercase tracking-wider font-semibold" style={{ color: theme.muted }}>
                  Collections
                </p>
                {collections.map((col) => (
                  <button
                    key={col.id}
                    onClick={() => onCollection?.(activeCollection === col.id ? "" : col.id)}
                    className="w-full flex items-center gap-2 text-sm text-left transition-colors duration-150"
                    style={{
                      paddingLeft: 16, paddingRight: 12, paddingTop: 10, paddingBottom: 10,
                      color: activeCollection === col.id ? theme.primary : theme.muted,
                      borderLeft: `2px solid ${activeCollection === col.id ? theme.primary : "transparent"}`,
                      backgroundColor: activeCollection === col.id ? theme.primary + "10" : undefined,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = theme.primary; e.currentTarget.style.backgroundColor = theme.primary + "10"; e.currentTarget.style.borderLeftColor = theme.primary; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = activeCollection === col.id ? theme.primary : theme.muted; e.currentTarget.style.backgroundColor = activeCollection === col.id ? theme.primary + "10" : "transparent"; e.currentTarget.style.borderLeftColor = activeCollection === col.id ? theme.primary : "transparent"; }}
                  >
                    {col.emoji && <span className="text-base leading-none">{col.emoji}</span>}
                    <span className="flex-1 truncate font-medium">{col.label}</span>
                    {col.productIds?.length > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: theme.primary + "20", color: theme.primary }}>
                        {col.productIds.length}
                      </span>
                    )}
                  </button>
                ))}
              </>
            )}
          </nav>
          {/* Mall Cart Button */}
          <button
            onClick={openCart}
            className="mx-3 my-2 px-4 py-3 rounded-xl border transition-all duration-200 hover:shadow-md flex items-center justify-between relative"
            style={{
              background: theme.card,
              borderColor: theme.border,
              color: theme.text,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.primary;
              e.currentTarget.style.background = theme.primary + "10";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = theme.border;
              e.currentTarget.style.background = theme.card;
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ShoppingBag size={18} style={{ color: theme.primary }} />
              <span style={{ fontSize: 14, fontWeight: 600 }}>Cart</span>
            </div>
            {cartCount > 0 && (
              <span
                style={{
                  background: theme.primary,
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: 999,
                  minWidth: 20,
                  textAlign: "center",
                }}
              >
                {cartCount}
              </span>
            )}
          </button>
          <div className="border-t" style={{ borderColor: theme.border }}>
            <a
              href={exitUrl}
              className="flex items-center gap-2 px-4 py-3 text-xs font-semibold transition-opacity hover:opacity-70"
              style={{ color: theme.muted }}
            >
              <ExternalLink size={13} />
              Exit Shop
            </a>
            <div className="px-4 pb-3 text-[10px]" style={{ color: theme.muted }}>
              Powered by Moondala
            </div>
          </div>
        </aside>

        {/* main */}
        <main className="flex-1 overflow-y-auto">{children}</main>
        
        {/* Mall Cart Drawer */}
        <MallCartDrawer theme={theme} isOpen={cartOpen} onClose={closeCart} />
      </div>
    );
  }

  /* ── TOP NAV (default + mobile) ── */
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: theme.bg, fontFamily: theme.font }}
    >
      {/* top bar */}
      <header
        className="sticky top-0 z-40 border-b backdrop-blur-md"
        style={{
          background: (theme.card || theme.bg) + "ee",
          borderColor: theme.border,
        }}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            {isMobile && navLinks.length > 0 && (
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? (
                  <X size={20} style={{ color: theme.text }} />
                ) : (
                  <Menu size={20} style={{ color: theme.text }} />
                )}
              </button>
            )}
            <button
              onClick={onHome}
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            >
              <ShoppingBag size={18} style={{ color: theme.primary }} />
              <span
                className="font-bold text-sm"
                style={{ color: theme.text }}
              >
                {shopName}
              </span>
            </button>
          </div>

          {/* desktop nav items */}
          {!isMobile && (
            <nav className="hidden md:flex items-center gap-1">
              <button
                onClick={onHome}
                className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg transition-all duration-200"
                style={{ color: theme.primary }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = theme.primary + '12';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Home size={15} />
                <span>Home</span>
              </button>
              {navLinks.slice(0, 6).map((item, i) => (
                <TopNavItem key={i} item={item} theme={theme} />
              ))}
            </nav>
          )}

          <div className="flex items-center gap-3">
            <a
              href={exitUrl}
              className="hidden sm:flex items-center gap-1 text-xs font-semibold px-2 py-1.5 rounded-lg transition-all duration-200"
              style={{ color: theme.muted }}
              onMouseEnter={(e) => { e.currentTarget.style.color = theme.text; e.currentTarget.style.backgroundColor = theme.primary + "12"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = theme.muted; e.currentTarget.style.backgroundColor = "transparent"; }}
            >
              <ExternalLink size={12} />
              Exit
            </a>
            <div className="hidden sm:block">{searchBar}</div>
            {/* Cart Icon */}
            <button
              onClick={openCart}
              className="relative p-2 rounded-lg transition-all duration-200"
              style={{ color: theme.text }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.primary + "15";
                e.currentTarget.style.color = theme.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = theme.text;
              }}
            >
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    background: theme.primary,
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "2px 5px",
                    borderRadius: 999,
                    minWidth: 16,
                    height: 16,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* mobile drawer */}
        {isMobile && mobileMenuOpen && (
          <div
            className="border-t py-2"
            style={{ borderColor: theme.border, background: theme.card }}
          >
            <div className="px-4 pb-3">{searchBar}</div>
            <SidebarNavItem
              item={{ label: "Home", icon: Home, onClick: () => { onHome?.(); setMobileMenuOpen(false); } }}
              theme={theme}
            />
            {navLinks.map((item, i) => (
              <SidebarNavItem key={i} item={item} theme={theme} onNavigate={(url) => { onNavigate?.(url); setMobileMenuOpen(false); }} />
            ))}
            {/* Collections (Mobile) */}
            {collections.length > 0 && (
              <>
                <div className="mx-4 my-1 border-t" style={{ borderColor: theme.border }} />
                <p className="px-4 pb-1 pt-2 text-[10px] uppercase tracking-wider font-semibold" style={{ color: theme.muted }}>Collections</p>
                {collections.map((col) => (
                  <button key={col.id} onClick={() => { onCollection?.(activeCollection === col.id ? "" : col.id); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left"
                    style={{ color: activeCollection === col.id ? theme.primary : theme.text }}
                  >
                    {col.emoji && <span>{col.emoji}</span>}
                    <span className="flex-1">{col.label}</span>
                    {col.productIds?.length > 0 && <span className="text-xs" style={{ color: theme.muted }}>{col.productIds.length}</span>}
                  </button>
                ))}
              </>
            )}
            {/* Exit Shop (Mobile) */}
            <div className="mx-4 my-1 border-t" style={{ borderColor: theme.border }} />
            <a href={exitUrl} className="flex items-center gap-2 px-4 py-2.5 text-sm" style={{ color: theme.muted }}>
              <ExternalLink size={14} /> Exit Shop
            </a>
            {/* Mall Cart Button (Mobile) */}
            <button
              onClick={() => { openCart(); setMobileMenuOpen(false); }}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:opacity-80 transition-opacity"
              style={{ color: theme.text }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <ShoppingBag size={18} style={{ color: theme.primary }} />
                <span style={{ fontSize: 14, fontWeight: 600 }}>Cart</span>
              </div>
              {cartCount > 0 && (
                <span
                  style={{
                    background: theme.primary,
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "2px 6px",
                    borderRadius: 999,
                    minWidth: 20,
                    textAlign: "center",
                  }}
                >
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>
      
      {/* Mall Cart Drawer */}
      <MallCartDrawer theme={theme} isOpen={cartOpen} onClose={closeCart} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SECTION RENDERERS
   ═══════════════════════════════════════════════════════════════ */





/* ── 3. Profile Header (Legacy - Social Style) ───────────────── */
function ProfileHeader({ data = {}, theme }) {
  const coverUrl = data.coverUrl || data.coverImage || data.cover || "";
  const avatarUrl = data.avatarUrl || data.avatar || data.logo || "";
  const name = data.shopName || data.name || "My Shop";
  const bio = data.bio || data.description || "";
  const headerFit = data.headerFit || "cover";
  const headerPosition = data.headerPosition || "center";
  const logoFit = data.logoFit || "cover";

  return (
    <div className="relative w-full">
      {/* cover */}
      <div
        className="w-full h-48 sm:h-56 relative"
        style={{
          backgroundImage: coverUrl ? `url(${toAbsUrl(coverUrl)})` : undefined,
          backgroundSize: headerFit === "fill" ? "100% 100%" : (headerFit || "cover"),
          backgroundPosition: headerPosition || "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: theme.primary + "20",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      </div>

      {/* avatar + info */}
      <div className="relative px-6 pb-6 -mt-16 flex items-end gap-4">
        {avatarUrl ? (
          <div
            className="w-24 h-24 rounded-2xl border-4 shadow-xl flex-shrink-0"
            style={{
              borderColor: theme.bg,
              backgroundImage: `url(${toAbsUrl(avatarUrl)})`,
              backgroundSize: logoFit === "contain" ? "contain" : "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundColor: theme.card || "#1a1a2e",
            }}
          />
        ) : (
          <div
            className="w-24 h-24 rounded-2xl border-4 flex items-center justify-center text-3xl font-bold shadow-xl flex-shrink-0"
            style={{
              borderColor: theme.bg,
              background: theme.primary,
              color: "#fff",
            }}
          >
            {name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="pb-1">
          <h1 className="text-xl font-bold" style={{ color: theme.text }}>
            {name}
          </h1>
          {bio && (
            <p
              className="text-sm mt-1 max-w-md"
              style={{ color: theme.muted }}
            >
              {bio}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── 4. Hero Banner ──────────────────────────────────────────── */
function HeroBanner({ data = {}, theme }) {
  const imageUrl =
    data.imageUrl || data.image || data.backgroundImage || "";
  const title = data.title || data.heading || "";
  const subtitle = data.subtitle || data.subheading || "";
  const btnText = data.buttonText || "";
  // No link set (or the old "#" default) → show all products (same as the user mall)
  const btnLink = (data.buttonLink && data.buttonLink !== "#") ? data.buttonLink : "?showAll=true";

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ minHeight: 280 }}
    >
      {imageUrl ? (
        <img
          src={toAbsUrl(imageUrl)}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${theme.primary}30, ${
              theme.accent || theme.primary
            }15)`,
          }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
      <div className="relative z-10 flex flex-col justify-center px-8 py-16 min-h-[280px]">
        {title && (
          <h2 className="text-3xl sm:text-4xl font-bold text-white max-w-lg leading-tight">
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-base text-white/80 mt-3 max-w-md">{subtitle}</p>
        )}
        {btnText && (
          <a
            href={safeHref(btnLink) || undefined}
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg"
            style={{ background: theme.primary, color: "#fff" }}
          >
            {btnText}
            <ArrowRight size={16} />
          </a>
        )}
      </div>
    </div>
  );
}

/* Only real external links open a new tab; "/path", "?query" and "#anchor" stay on the page */
function newTabProps(href) {
  return /^(https?:)?\/\//i.test(String(href || "")) ? { target: "_blank", rel: "noopener noreferrer" } : {};
}

/* Preview detail-page link for a product card (ProductGrid + FeaturedProduct) */
function previewProductUrl(product, theme) {
  const productId = product._id || product.id;
  const linkProductId = product._parentId || productId;
  const sidebarPct = Math.min(100, Math.max(0, Number(product._discountPct) || 0));
  const variantParam = product._variantId
    ? `&variantId=${encodeURIComponent(product._variantId)}`
    : '';
  const discountParam = sidebarPct > 0
    ? `&discount=${sidebarPct}${
        Array.isArray(product._discountedVariantIds) && product._discountedVariantIds.length > 0
          ? `&dvids=${product._discountedVariantIds.join(',')}` : ''
      }`
    : '';
  return `/mall/preview/product/${encodeURIComponent(linkProductId)}?theme=${theme.id}${variantParam}${discountParam}`;
}

/* Price / sale / sidebar-discount info (ProductGrid + FeaturedProduct) */
function previewPriceInfo(product) {
  const price = product.price ?? product.salePrice ?? "";
  const currency = String(product.currency || "USD").toUpperCase();
  const originalPrice =
    product._originalPrice || product.originalPrice || product.compareAtPrice || "";
  const sidebarPct = Math.min(100, Math.max(0, Number(product._discountPct) || 0));
  const onSale = !!(sidebarPct > 0
    || (originalPrice && Number(originalPrice) > Number(price)));
  const discount = sidebarPct > 0
    ? sidebarPct
    : (onSale ? Math.round((1 - Number(price) / Number(originalPrice)) * 100) : 0);
  const isPartial = !!product._partialDiscount;
  const displayOriginalPrice = sidebarPct > 0 ? product._originalPrice : originalPrice;
  return { price, currency, originalPrice, sidebarPct, onSale, discount, isPartial, displayOriginalPrice };
}

/* ── 5. Product Grid ─────────────────────────────────────────── */
function ProductGrid({ data = {}, theme }) {
  const title = data.title || "";
  const products = Array.isArray(data.products) ? data.products : [];
  const dark = isDark(theme);

  const getGallery = (product) => {
    const seen = new Set();
    const out = [];
    const asImageValue = (item) => {
      if (!item) return "";
      if (typeof item === "string") return item;
      if (typeof item === "object") {
        return String(item.url || item.secure_url || item.path || item.image || "").trim();
      }
      return "";
    };
    const add = (u) => {
      const s = asImageValue(u);
      if (!s || seen.has(s)) return;
      seen.add(s);
      out.push(s);
    };
    (Array.isArray(product?.images) ? product.images : []).forEach(add);
    const variantImages = Array.isArray(product?.variants)
      ? product.variants.flatMap((v) => (Array.isArray(v?.images) ? v.images : []))
      : [];
    variantImages.forEach(add);
    if (product?.image) add(product.image);
    if (product?.imageUrl) add(product.imageUrl);
    return out.slice(0, 8);
  };

  const getOptionValues = (product, optionName) => {
    const key = String(optionName || "").toLowerCase();
    const fromOptions = (Array.isArray(product?.options) ? product.options : []).find(
      (o) => String(o?.name || "").toLowerCase() === key
    );
    if (Array.isArray(fromOptions?.values) && fromOptions.values.length > 0) {
      return [...new Set(fromOptions.values.map((v) => String(v || "").trim()).filter(Boolean))];
    }

    const fromVariants = (Array.isArray(product?.variants) ? product.variants : [])
      .flatMap((v) => (Array.isArray(v?.optionValues) ? v.optionValues : []))
      .filter((ov) => String(ov?.name || "").toLowerCase() === key)
      .map((ov) => String(ov?.value || "").trim())
      .filter(Boolean);
    return [...new Set(fromVariants)];
  };

  return (
    <div className="px-4 sm:px-6 py-8">
      {title && (
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold" style={{ color: theme.text }}>
            {title}
          </h2>
          {products.length > 4 && (
            <a
              href="?showAll=true"
              className="text-xs font-medium"
              style={{ color: theme.primary, textDecoration: "none" }}
            >
              View All →
            </a>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {products.length > 0
          ? products.map((product, i) => {
              const gallery = getGallery(product);
              // Single image: the card's own image if set, else the first gallery image
              const img = product.image || product.imageUrl || gallery[0] || "";
              const pName = product.title || product.name || "Product";
              const colors = getOptionValues(product, "color").slice(0, 5);
              // Size chips: prefer "size" option, else extract size-like values from variants
              const SIZE_RX = /^(xs|s|m|l|xl|xxl|xxxl|2xl|3xl|4xl|5xl|small|medium|large|x-large|xx-large|\d+(?:\.\d+)?)$/i;
              let sizes = getOptionValues(product, "size").slice(0, 5);
              if (sizes.length === 0) {
                sizes = [...new Set(
                  (Array.isArray(product?.variants) ? product.variants : [])
                    .flatMap((v) => (v.optionValues || []).map((ov) => String(ov?.value || "").trim()))
                    .filter((v) => v && SIZE_RX.test(v))
                )].slice(0, 5);
              }
              const { price, currency, sidebarPct, onSale, discount, isPartial, displayOriginalPrice } = previewPriceInfo(product);
              const productId = product._id || product.id;

              // CSS swatch colour mapping (for clickable colour dots)
              const CSS_COLOR_MAP = {
                red:'#ef4444', crimson:'#dc143c', pink:'#ec4899', rose:'#f43f5e',
                orange:'#f97316', amber:'#f59e0b', yellow:'#eab308', gold:'#d97706',
                lime:'#84cc16', green:'#22c55e', emerald:'#10b981', teal:'#14b8a6',
                cyan:'#06b6d4', sky:'#0ea5e9', blue:'#3b82f6', indigo:'#6366f1',
                violet:'#8b5cf6', purple:'#a855f7', fuchsia:'#d946ef', magenta:'#e879f9',
                brown:'#92400e', tan:'#d4a574', beige:'#f5f0e8', cream:'#fffdd0',
                white:'#ffffff', ivory:'#fffff0', silver:'#c0c0c0',
                gray:'#9ca3af', grey:'#9ca3af', charcoal:'#374151',
                black:'#111827', navy:'#1e3a5f', maroon:'#7f1d1d',
                khaki:'#c3b17a', olive:'#6b7280', mint:'#98fbc5', coral:'#f4694b',
              };
              const cssColor = (name) => CSS_COLOR_MAP[String(name || "").toLowerCase().trim()] || "#9ca3af";

              return (
                <a
                  key={productId || i}
                  href={previewProductUrl(product, theme)}
                  className="group rounded-xl overflow-hidden border transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer block"
                  style={{
                    background: theme.card,
                    borderColor: theme.border,
                    textDecoration: 'none',
                  }}
                >
                  <div className="relative aspect-square overflow-hidden" style={{ background: theme.primary + "10" }}>
                    {img ? (
                      <img
                        src={toAbsUrl(img)}
                        alt={pName}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag size={28} style={{ color: theme.muted }} />
                      </div>
                    )}
                    {/* compareAtPrice sale badge (top-left) */}
                    {onSale && sidebarPct === 0 && (
                      <span
                        className="absolute top-2 left-2 px-2 py-0.5 rounded-lg text-[10px] font-bold text-white"
                        style={{ background: "#ef4444" }}
                      >
                        -{discount}%
                      </span>
                    )}
                    {/* customSidebar / per-variant discount badge (top-right) */}
                    {sidebarPct > 0 && (
                      <span
                        className="absolute top-2 right-2 px-2 py-0.5 rounded-lg text-[10px] font-bold text-white"
                        style={{ background: "#f97316" }}
                      >
                        {isPartial ? `from ${sidebarPct}% OFF` : `${sidebarPct}% OFF`}
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <h3
                      className="text-sm font-medium truncate"
                      style={{ color: theme.text }}
                    >
                      {pName}
                    </h3>
                    <div className="flex items-center gap-2 mt-1.5">
                      {price !== "" && (
                        <span
                          className="text-sm font-bold"
                          style={{ color: (onSale && !isPartial) ? "#ef4444" : theme.primary }}
                        >
                          {currency} {Number(price).toFixed(2)}
                        </span>
                      )}
                      {onSale && !isPartial && displayOriginalPrice != null && displayOriginalPrice !== "" && (
                        <span
                          className="text-xs line-through"
                          style={{ color: theme.muted }}
                        >
                          {currency} {Number(displayOriginalPrice).toFixed(2)}
                        </span>
                      )}
                    </div>

                    {/* Colour swatches (clickable circles) */}
                    {colors.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        {colors.map((color) => (
                          <span
                            key={`${productId || i}-c-${color}`}
                            title={color}
                            style={{
                              width: 16, height: 16, borderRadius: "50%",
                              background: cssColor(color),
                              border: "1.5px solid rgba(0,0,0,0.15)",
                              display: "inline-block",
                            }}
                          />
                        ))}
                      </div>
                    )}

                    {/* Size chips */}
                    {sizes.length > 0 && (
                      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                        {sizes.map((size) => (
                          <span
                            key={`${productId || i}-s-${size}`}
                            style={{
                              fontSize: 10, fontWeight: 700,
                              padding: "2px 6px", borderRadius: 4,
                              background: theme.primary + "18",
                              color: theme.primary,
                              border: `1px solid ${theme.primary}30`,
                            }}
                          >
                            {size}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </a>
              );
            })
          : /* placeholder cards */
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={`ph-${i}`}
                className="rounded-xl overflow-hidden border"
                style={{ background: theme.card, borderColor: theme.border }}
              >
                <div
                  className="aspect-square flex items-center justify-center"
                  style={{ background: dark ? "#ffffff08" : "#00000006" }}
                >
                  <ShoppingBag size={24} style={{ color: theme.border }} />
                </div>
                <div className="p-3 space-y-2">
                  <div
                    className="h-3 rounded-full w-3/4"
                    style={{ background: theme.border }}
                  />
                  <div
                    className="h-3 rounded-full w-1/3"
                    style={{ background: theme.border }}
                  />
                </div>
              </div>
            ))}
      </div>
    </div>
  );
}

/* ── 4. Link List (Linktree-style) ───────────────────────────── */
function LinkList({ data = {}, theme }) {
  const title = data.title || "";
  const links = Array.isArray(data.links) ? data.links : [];

  return (
    <div className="px-6 py-8 max-w-md mx-auto">
      {title && (
        <h2
          className="text-lg font-bold text-center mb-5"
          style={{ color: theme.text }}
        >
          {title}
        </h2>
      )}
      <div className="space-y-3">
        {links.map((link, i) => (
          <a
            key={i}
            href={safeHref(link.url) || "#"}
            {...newTabProps(safeHref(link.url))}
            className="flex items-center justify-between px-5 py-3.5 rounded-xl border text-sm font-medium transition-all duration-300 hover:scale-[1.02] hover:shadow-md"
            style={{
              background: theme.card,
              borderColor: theme.border,
              color: theme.text,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.primary;
              e.currentTarget.style.background = theme.primary + "10";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = theme.border;
              e.currentTarget.style.background = theme.card;
            }}
          >
            <span>{link.label || link.title || link.url}</span>
            <ExternalLink size={14} style={{ color: theme.muted }} />
          </a>
        ))}
        {links.length === 0 && (
          <div
            className="text-center py-8 text-sm rounded-xl border-2 border-dashed"
            style={{ color: theme.muted, borderColor: theme.border }}
          >
            No links added yet
          </div>
        )}
      </div>
    </div>
  );
}

/* ── 5. Rich Text ────────────────────────────────────────────── */
function RichText({ data = {}, theme }) {
  const content = data.content || data.text || data.body || "";
  const align = data.textAlign || data.align || "left";

  return (
    <div
      className="px-6 py-8 prose prose-sm max-w-none"
      style={{ color: theme.text, textAlign: align }}
      dangerouslySetInnerHTML={
        content.includes("<") ? { __html: sanitizeHtml(content) } : undefined
      }
    >
      {!content.includes("<") ? (
        <p style={{ color: theme.text, whiteSpace: "pre-wrap" }}>{content}</p>
      ) : null}
    </div>
  );
}

/* ── 6. NavigationMenu (logical – renders nothing in main flow) ─ */
function NavigationMenu() {
  return null;
}

/* ═══════════════════════════════════════════════════════════════
   NEW SECTION TYPES
   ═══════════════════════════════════════════════════════════════ */

/* ── 7. Sale Banner ──────────────────────────────────────────── */
function SaleBanner({ data = {}, theme }) {
  const title = data.title || "🔥 SALE";
  const subtitle = data.subtitle || "Limited time offer";
  const discount = data.discount || "";
  const btnText = data.buttonText || "";
  const btnLink = (data.buttonLink && data.buttonLink !== "#") ? data.buttonLink : "?showAll=true";
  const bannerStyle = data.bannerStyle || "gradient"; // gradient | bold | minimal
  const bgImage = data.imageUrl || data.image || "";

  const backgrounds = {
    gradient: "linear-gradient(135deg, #ef4444, #f97316, #eab308)",
    bold: `linear-gradient(135deg, ${theme.primary}, ${
      theme.accent || theme.primary
    })`,
    minimal: theme.card,
  };

  return (
    <div
      className="relative overflow-hidden"
      style={{
        background: bgImage
          ? undefined
          : backgrounds[bannerStyle] || backgrounds.gradient,
        minHeight: 140,
      }}
    >
      {bgImage && (
        <>
          <img
            src={toAbsUrl(bgImage)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </>
      )}
      {/* animated sparkles for gradient style */}
      {bannerStyle === "gradient" && !bgImage && (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl animate-pulse" />
          <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse" />
        </div>
      )}
      <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between px-6 sm:px-8 py-8 gap-4">
        <div className="text-center sm:text-left">
          <div className="flex items-center gap-2 mb-1">
            {bannerStyle === "gradient" && (
              <Flame size={20} className="text-yellow-200" />
            )}
            {bannerStyle === "bold" && (
              <Tag size={20} className="text-white/80" />
            )}
            <span
              className="text-xs font-bold uppercase tracking-wider"
              style={{
                color:
                  bannerStyle === "minimal" ? theme.primary : "#ffffffcc",
              }}
            >
              {subtitle}
            </span>
          </div>
          <h2
            className="text-2xl sm:text-3xl font-extrabold"
            style={{
              color: bannerStyle === "minimal" ? theme.text : "#ffffff",
            }}
          >
            {title}
          </h2>
          {discount && (
            <div className="flex items-center gap-2 mt-2">
              <Percent
                size={16}
                style={{
                  color:
                    bannerStyle === "minimal" ? theme.primary : "#fbbf24",
                }}
              />
              <span
                className="text-lg font-bold"
                style={{
                  color:
                    bannerStyle === "minimal" ? theme.primary : "#fbbf24",
                }}
              >
                {discount}
              </span>
            </div>
          )}
        </div>
        {btnText && (
          <a
            href={safeHref(btnLink) || undefined}
            className="px-6 py-3 rounded-xl font-bold text-sm transition-all duration-300 hover:scale-105 shadow-lg"
            style={{
              background: bannerStyle === "minimal" ? theme.primary : "#ffffff",
              color: bannerStyle === "minimal" ? "#ffffff" : "#111111",
            }}
          >
            {btnText}
          </a>
        )}
      </div>
    </div>
  );
}

/* ── 8. Announcement Bar ─────────────────────────────────────── */
function AnnouncementBar({ data = {}, theme }) {
  const text = data.text || data.content || data.announcement || "📢 Announcement";
  const link = data.link || data.url || "";
  const barStyle = data.barStyle || data.style || "primary"; // primary | warning | info | subtle
  
  // Debug announcement data
  console.log('📢 Announcement data:', { text, link, barStyle, rawData: data });

  const barStyles = {
    primary: { bg: theme.primary, color: "#ffffff" },
    warning: { bg: "#f59e0b", color: "#1c1917" },
    info: { bg: "#3b82f6", color: "#ffffff" },
    subtle: { bg: theme.card, color: theme.text },
  };

  const s = barStyles[barStyle] || barStyles.primary;

  const content = (
    <div
      className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-center"
      style={{ background: s.bg, color: s.color }}
    >
      <Sparkles size={14} className="opacity-70 flex-shrink-0" />
      <span>{text}</span>
      {link && (
        <ArrowRight size={14} className="opacity-70 flex-shrink-0" />
      )}
    </div>
  );

  return link ? (
    <a
      href={safeHref(link) || undefined}
      {...newTabProps(safeHref(link))}
      className="block hover:opacity-90 transition-opacity"
    >
      {content}
    </a>
  ) : (
    content
  );
}

/* ── 9. Featured Product (single product spotlight) ──────────── */
function FeaturedProduct({ data = {}, theme }) {
  const products = Array.isArray(data.products) ? data.products : [];
  const product = products[0] || null;
  const hasPick = Array.isArray(data.productIds) && data.productIds.length > 0;

  if (!product) {
    // Same rule as the user mall: no product → section is hidden there.
    // In the editor preview show a hint so the shop knows why.
    if (hasPick) return null;
    return (
      <div
        className="mx-4 sm:mx-6 my-4 rounded-2xl border-2 border-dashed p-8 flex flex-col items-center justify-center gap-2 text-center"
        style={{ borderColor: theme.border }}
      >
        <ShoppingBag size={32} style={{ color: theme.muted }} />
        <span className="text-xs" style={{ color: theme.muted }}>
          Pick a product to feature. This section stays hidden on your mall until you do.
        </span>
      </div>
    );
  }

  const img =
    product.image ||
    product.imageUrl ||
    product.images?.[0] ||
    data.imageUrl ||
    "";
  // Shop's own title/description override the product's (old saved default "Featured" = no override)
  const titleOverride = data.title && String(data.title).trim() !== "Featured" ? String(data.title).trim() : "";
  const name = titleOverride || product.title || product.name || "Featured Product";
  const desc = data.subtitle || product.description || "";
  const { price, currency, sidebarPct, onSale, discount, isPartial, displayOriginalPrice } = previewPriceInfo(product);
  const productUrl = previewProductUrl(product, theme);
  const btnText = data.buttonText || "Shop Now";
  const btnLink = (data.buttonLink && data.buttonLink !== "#") ? data.buttonLink : productUrl;
  const autoBadge = sidebarPct > 0
    ? (isPartial ? `from ${sidebarPct}% OFF` : `${sidebarPct}% OFF`)
    : (onSale ? `-${discount}%` : "");
  const badge = data.badge || autoBadge;

  return (
    <div
      className="flex flex-col sm:flex-row items-stretch overflow-hidden border rounded-2xl mx-4 sm:mx-6 my-4"
      style={{ background: theme.card, borderColor: theme.border }}
    >
      {/* image */}
      <a
        href={productUrl}
        className="relative sm:w-1/2 aspect-square sm:aspect-auto overflow-hidden block"
      >
        {img ? (
          <img
            src={toAbsUrl(img)}
            alt={name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="w-full h-full min-h-[200px] flex items-center justify-center"
            style={{ background: theme.primary + "15" }}
          >
            <ShoppingBag size={48} style={{ color: theme.muted }} />
          </div>
        )}
        {badge && (
          <span
            className="absolute top-3 left-3 px-3 py-1 rounded-lg text-xs font-bold text-white"
            style={{ background: data.badge ? theme.primary : (sidebarPct > 0 ? "#f97316" : "#ef4444") }}
          >
            {badge}
          </span>
        )}
      </a>
      {/* info */}
      <div className="sm:w-1/2 flex flex-col justify-center p-6 sm:p-8">
        <a href={productUrl} style={{ textDecoration: "none" }}>
          <h2
            className="text-xl sm:text-2xl font-bold"
            style={{ color: theme.text }}
          >
            {name}
          </h2>
        </a>
        {desc && (
          <p
            className="text-sm mt-2 line-clamp-3"
            style={{ color: theme.muted }}
          >
            {desc}
          </p>
        )}
        <div className="flex items-center gap-3 mt-4">
          {price !== "" && (
            <span
              className="text-2xl font-extrabold"
              style={{ color: (onSale && !isPartial) ? "#ef4444" : theme.primary }}
            >
              {currency} {Number(price).toFixed(2)}
            </span>
          )}
          {onSale && !isPartial && displayOriginalPrice != null && displayOriginalPrice !== "" && (
            <span
              className="text-sm line-through"
              style={{ color: theme.muted }}
            >
              {currency} {Number(displayOriginalPrice).toFixed(2)}
            </span>
          )}
        </div>
        {btnText && (
          <a
            href={safeHref(btnLink) || undefined}
            className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-xl font-semibold text-sm text-white transition-all duration-300 hover:scale-105 hover:shadow-lg self-start"
            style={{ background: theme.primary }}
          >
            {btnText}
            <ArrowRight size={16} />
          </a>
        )}
      </div>
    </div>
  );
}

/* ── 10. Testimonials ────────────────────────────────────────── */
function Testimonials({ data = {}, theme }) {
  const title = data.title || "";
  const items = Array.isArray(data.testimonials)
    ? data.testimonials
    : Array.isArray(data.items)
    ? data.items
    : [];

  return (
    <div className="px-6 py-10">
      {title && (
        <h2
          className="text-lg font-bold text-center mb-8"
          style={{ color: theme.text }}
        >
          {title}
        </h2>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
        {items.length > 0
          ? items.map((item, i) => (
              <div
                key={i}
                className="p-5 rounded-xl border"
                style={{ background: theme.card, borderColor: theme.border }}
              >
                <Quote
                  size={20}
                  className="mb-3 opacity-30"
                  style={{ color: theme.primary }}
                />
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: theme.text }}
                >
                  &ldquo;{item.text || item.quote || item.content || ""}
                  &rdquo;
                </p>
                <div className="mt-4 flex items-center gap-3">
                  {item.avatar && (
                    <img
                      src={toAbsUrl(item.avatar)}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <p
                      className="text-xs font-semibold"
                      style={{ color: theme.text }}
                    >
                      {item.name || item.author || "Customer"}
                    </p>
                    {item.rating && (
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, s) => (
                          <Star
                            key={s}
                            size={10}
                            fill={
                              s < Number(item.rating) ? "#fbbf24" : "none"
                            }
                            stroke={
                              s < Number(item.rating)
                                ? "#fbbf24"
                                : theme.muted
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          : /* placeholder */
            Array.from({ length: 2 }).map((_, i) => (
              <div
                key={i}
                className="p-5 rounded-xl border-2 border-dashed flex items-center justify-center"
                style={{ borderColor: theme.border, minHeight: 120 }}
              >
                <span className="text-xs" style={{ color: theme.muted }}>
                  Add a testimonial
                </span>
              </div>
            ))}
      </div>
    </div>
  );
}

/* ── 11. Image Gallery ───────────────────────────────────────── */
function ImageGallery({ data = {}, theme, shopData }) {
  const title = data.title || "";
  const images = Array.isArray(data.images) ? data.images : [];
  const layout = data.layout || "grid"; // grid | masonry

  return (
    <div className="px-6 py-8">
      {title && (
        <h2
          className="text-lg font-bold mb-5"
          style={{ color: theme.text }}
        >
          {title}
        </h2>
      )}
      <div
        className={`gap-3 ${
          layout === "masonry"
            ? "columns-2 sm:columns-3 space-y-3"
            : "grid grid-cols-2 sm:grid-cols-3"
        }`}
      >
        {images.length > 0
          ? images.map((img, i) => {
              const src = typeof img === "string" ? img : img.url || img.src || "";
              const caption = typeof img === "object" ? img.caption || "" : "";
              const rawProductId = typeof img === "object" ? img.productId || "" : "";

              // Support both legacy plain IDs and new "parentId__variantId" format
              let productUrl = "";
              if (rawProductId) {
                const [parentId, variantId] = rawProductId.split("__");
                const variantParam = variantId ? `&variantId=${encodeURIComponent(variantId)}` : "";
                productUrl = `/mall/preview/product/${encodeURIComponent(parentId)}?theme=${theme.id || "midnight"}${variantParam}`;
              } else if (typeof img === "object" && img.productUrl) {
                productUrl = img.productUrl;
              }

              const imageContent = (
                <div
                  className={`rounded-xl overflow-hidden border group ${
                    layout === "masonry" ? "break-inside-avoid mb-3" : ""
                  } ${productUrl ? "cursor-pointer hover:shadow-lg" : ""}`}
                  style={{ borderColor: theme.border }}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={toAbsUrl(src)}
                      alt={caption || `Image ${i + 1}`}
                      className={`w-full object-cover group-hover:scale-105 transition-all duration-500 ${
                        layout === "masonry" ? "" : "aspect-square"
                      }`}
                    />
                    {productUrl && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="bg-white/90 px-3 py-1.5 rounded-full shadow-lg">
                          <span className="text-xs font-medium text-gray-800">
                            View Product
                          </span>
                        </div>
                      </div>
                    )}
                    {caption && (
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <p className="text-xs text-white font-medium">
                          {caption}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );

              return productUrl ? (
                <a
                  key={i}
                  href={productUrl}
                  className="block hover:transform hover:-translate-y-1 transition-transform duration-300 cursor-pointer"
                  onClick={() => {}}
                >
                  {imageContent}
                </a>
              ) : (
                <div key={i}>
                  {imageContent}
                </div>
              );
            })
          : Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-xl border-2 border-dashed flex items-center justify-center"
                style={{ borderColor: theme.border }}
              >
                <span className="text-xs" style={{ color: theme.muted }}>
                  +
                </span>
              </div>
            ))}
      </div>
    </div>
  );
}

/* ── 12. Divider ─────────────────────────────────────────────── */
function Divider({ data = {}, theme }) {
  const dividerStyle = data.dividerStyle || "line"; // line | dots | space | gradient
  const spacing = data.spacing || "md"; // sm | md | lg

  const py = { sm: "py-3", md: "py-6", lg: "py-12" };

  if (dividerStyle === "space") {
    return <div className={py[spacing] || py.md} />;
  }

  return (
    <div
      className={`px-6 ${
        py[spacing] || py.md
      } flex items-center justify-center`}
    >
      {dividerStyle === "dots" ? (
        <div className="flex items-center gap-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: theme.border }}
            />
          ))}
        </div>
      ) : dividerStyle === "gradient" ? (
        <div
          className="w-full h-px"
          style={{
            background: `linear-gradient(to right, transparent, ${theme.border}, transparent)`,
          }}
        />
      ) : (
        <div className="w-full h-px" style={{ background: theme.border }} />
      )}
    </div>
  );
}

/* ── 13. Video Embed ─────────────────────────────────────────── */
function VideoEmbed({ data = {}, theme }) {
  const title = data.title || "";
  const url = data.videoUrl || data.url || "";
  const autoplay = !!data.autoplay;

  // aspect ratio: "16:9" | "4:3" | "1:1" | "9:16" | "21:9"
  const aspectMap = {
    "16:9": "56.25%",
    "4:3":  "75%",
    "1:1":  "100%",
    "9:16": "177.78%",
    "21:9": "42.86%",
  };
  const paddingBottom = aspectMap[data.aspectRatio] || "56.25%";

  // Detect direct video file (mp4, webm, mov, ogg, mkv)
  const isDirectVideo = /\.(mp4|webm|mov|ogg|mkv)(\?|$)/i.test(url);

  const getEmbedUrl = (rawUrl) => {
    if (!rawUrl) return "";
    const ytMatch = rawUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) {
      const ap = autoplay ? "&autoplay=1&mute=1" : "";
      return `https://www.youtube.com/embed/${ytMatch[1]}?rel=0${ap}`;
    }
    const vimeoMatch = rawUrl.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      const ap = autoplay ? "&autoplay=1&muted=1" : "";
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?${ap}`;
    }
    return rawUrl;
  };

  const embedUrl = isDirectVideo ? "" : getEmbedUrl(url);

  return (
    <div className="px-6 py-8">
      {title && (
        <h2 className="text-lg font-bold mb-4" style={{ color: theme.text }}>
          {title}
        </h2>
      )}
      {url ? (
        isDirectVideo ? (
          /* ── Direct video file — aspect ratio box with object-cover (no black bars) ── */
          <div className="relative w-full rounded-xl overflow-hidden border" style={{ borderColor: theme.border, paddingBottom }}>
            <video
              src={toAbsUrl(url)}
              className="absolute inset-0 w-full h-full"
              style={{ objectFit: "cover" }}
              autoPlay={autoplay}
              muted={autoplay}
              loop={autoplay}
              playsInline
              controls={!autoplay}
            />
          </div>
        ) : (
          /* ── YouTube / Vimeo / other embed ── */
          <div className="relative w-full rounded-xl overflow-hidden border" style={{ borderColor: theme.border, paddingBottom }}>
            <iframe
              src={embedUrl}
              title={title || "Video"}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )
      ) : (
        <div
          className="w-full aspect-video rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2"
          style={{ borderColor: theme.border }}
        >
          <Play size={32} style={{ color: theme.muted }} />
          <span className="text-xs" style={{ color: theme.muted }}>
            Add a video URL (.mp4 for autoplay, or YouTube/Vimeo)
          </span>
        </div>
      )}
    </div>
  );
}

/* ── 14. Countdown ───────────────────────────────────────────── */
function Countdown({ data = {}, theme }) {
  const title = data.title || "⏰ Sale Ends In";
  const endDate = data.endDate || data.deadline || data.end_date || "";
  const [timeLeft, setTimeLeft] = useState({ d: 0, h: 0, m: 0, s: 0 });
  


  useEffect(() => {
    if (!endDate) return;
    // Accepts ISO strings (new) and datetime-local "YYYY-MM-DDTHH:mm" (old, read as local time)
    const target = new Date(endDate).getTime();
    if (!Number.isFinite(target)) return;
    const tick = () => {
      const diff = Math.max(0, target - Date.now());
      setTimeLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endDate]);

  const units = [
    { label: "Days", val: timeLeft.d },
    { label: "Hours", val: timeLeft.h },
    { label: "Mins", val: timeLeft.m },
    { label: "Secs", val: timeLeft.s },
  ];

  return (
    <div className="px-6 py-8">
      <h2
        className="text-lg font-bold text-center mb-6"
        style={{ color: theme.text }}
      >
        {title}
      </h2>
      <div className="flex items-center justify-center gap-2 sm:gap-3">
        {units.map(({ label, val }) => (
          <div key={label} className="text-center">
            <div
              className="w-12 h-12 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center text-xl sm:text-3xl font-extrabold border"
              style={{
                background: theme.card,
                borderColor: theme.border,
                color: theme.primary,
              }}
            >
              {endDate ? String(val).padStart(2, "0") : "--"}
            </div>
            <span
              className="text-[10px] uppercase tracking-wider mt-1.5 block"
              style={{ color: theme.muted }}
            >
              {label}
            </span>
          </div>
        ))}
      </div>
      {!endDate && (
        <p
          className="text-xs text-center mt-4"
          style={{ color: theme.muted }}
        >
          Set an end date to activate the countdown
        </p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   COMPONENT MAP  –  section type → React component
   ═══════════════════════════════════════════════════════════════ */
export const COMPONENT_MAP = {
  ProfileHeader,
  profileheader: ProfileHeader,
  profile_header: ProfileHeader,

  HeroBanner,
  herobanner: HeroBanner,
  hero_banner: HeroBanner,
  hero_text: HeroBanner,

  ProductGrid,
  productgrid: ProductGrid,
  product_grid: ProductGrid,
  featured_products: ProductGrid,

  LinkList,
  linklist: LinkList,
  link_list: LinkList,

  RichText,
  richtext: RichText,
  rich_text: RichText,

  NavigationMenu,
  navigationmenu: NavigationMenu,

  /* ── new section types ── */
  SaleBanner,
  salebanner: SaleBanner,
  sale_banner: SaleBanner,

  AnnouncementBar,
  announcementbar: AnnouncementBar,
  announcement_bar: AnnouncementBar,

  FeaturedProduct,
  featuredproduct: FeaturedProduct,
  featured_product: FeaturedProduct,

  Testimonials,
  testimonials: Testimonials,

  ImageGallery,
  imagegallery: ImageGallery,
  image_gallery: ImageGallery,

  Divider,
  divider: Divider,

  VideoEmbed,
  videoembed: VideoEmbed,
  video_embed: VideoEmbed,

  Countdown,
  countdown: Countdown,
};

/* ═══════════════════════════════════════════════════════════════
   SECTION METADATA  –  used by the editor for labels, icons, descriptions
   ═══════════════════════════════════════════════════════════════ */
export const SECTION_META = {
  ProfileHeader: {
    label: "Profile Header",
    icon: "User",
    description: "Cover image, avatar, name & bio",
    category: "essentials",
  },
  HeroBanner: {
    label: "Hero Banner",
    icon: "Image",
    description: "Full-width banner with text overlay & CTA",
    category: "essentials",
  },
  ProductGrid: {
    label: "Product Grid",
    icon: "Grid3X3",
    description: "Display products in a responsive grid",
    category: "products",
  },
  FeaturedProduct: {
    label: "Featured Product",
    icon: "Star",
    description: "Spotlight a single product with details",
    category: "products",
  },
  SaleBanner: {
    label: "Sale Banner",
    icon: "Flame",
    description: "Eye-catching sale / clearance promotion",
    category: "promotions",
  },
  AnnouncementBar: {
    label: "Announcement",
    icon: "Megaphone",
    description: "Slim notification bar for alerts & promos",
    category: "promotions",
  },
  Countdown: {
    label: "Countdown Timer",
    icon: "Clock",
    description: "Countdown to a sale or event deadline",
    category: "promotions",
  },
  Testimonials: {
    label: "Testimonials",
    icon: "Quote",
    description: "Customer reviews & social proof",
    category: "content",
  },
  LinkList: {
    label: "Link List",
    icon: "Link",
    description: "Linktree-style list of buttons",
    category: "content",
  },
  RichText: {
    label: "Rich Text",
    icon: "Type",
    description: "Free-form text content block",
    category: "content",
  },
  ImageGallery: {
    label: "Image Gallery",
    icon: "Images",
    description: "Photo grid with product links & captions",
    category: "content",
  },
  VideoEmbed: {
    label: "Video",
    icon: "Play",
    description: "Embed YouTube or Vimeo videos",
    category: "content",
  },
  Divider: {
    label: "Divider",
    icon: "Minus",
    description: "Visual separator between sections",
    category: "layout",
  },
  NavigationMenu: {
    label: "Sidebar Nav",
    icon: "Menu",
    description: "Category navigation menu",
    category: "layout",
  },
};
